import React, { useMemo } from 'react';
import { SubType } from '@erecruit/ui-kit/src/utils/tsHelpers';
import {
  Requests,
  Resources,
  Mutations,
  AppPermissions,
} from '@erecruit/types';
import { Intl } from '@erecruit/core';
import {
  Flex,
  FlexItem,
  Icon,
  Icons,
  Status,
  Tag,
  Text,
  Tooltip,
} from '@erecruit/ui-kit';
import {
  ColumnFilters,
  dateRender,
  TableDateRangeFilter,
  OnDateRangeFilter,
  ResetDateFilter,
  TableColumn,
  Departments,
  ThemedLink,
  Authorize,
} from '@erecruit/modules';

import { REJECTED_STATUS_ID, SUBMITTAL_STATUS_ID } from './constants';
import { MatchSearchFilterData } from './state/reducer';
import { MatchParams } from '../../App/types';
import { ApprovalPathStatuses } from '../ApprovalPath';
import { renderApprovalPathColumn } from '../PositionSearch/columns';

const { FormattedMessage, injectIntl, strToLocalizationKeys } = Intl;

const messages = messageIds.vms.matchSearch;

type SearchParamsKey = keyof SubType<Resources.MatchSearchFilter, number[]>;

export type MatchColumnType = TableColumn<
  Resources.MatchSearch,
  number,
  Resources.MatchSearchFilter,
  number[]
>;

const allColumns = [
  nameof<Resources.MatchSearch>(o => o.id),
  nameof.full<Resources.MatchSearch>(o => o.candidate.name),
  nameof.full<Resources.MatchSearch>(o => o.position.name),
  nameof.full<Resources.MatchSearch>(o => o.status.name),
  nameof<Resources.MatchSearch>(o => o.estimatedStartDate),
  nameof<Resources.MatchSearch>(o => o.estimatedEndDate),
  nameof<Resources.MatchSearch>(o => o.createdOn),
];

export const defaultVisibleColumns: Record<MatchParams, string[]> = {
  All: allColumns,
  EndingSoon: allColumns,
  PendingStart: allColumns,
  PendingMyApproval: allColumns,
  Submittal: [
    nameof<Resources.MatchSearch>(o => o.id),
    nameof.full<Resources.MatchSearch>(o => o.candidate.name),
    nameof.full<Resources.MatchSearch>(o => o.position.name),
    nameof.full<Resources.MatchSearch>(o => o.status.name),
    nameof.full<Resources.MatchSearch>(o => o.positionType.name),
    nameof<Resources.MatchSearch>(o => o.estimatedStartDate),
    nameof<Resources.MatchSearch>(o => o.submittedOn),
  ],
  FullPlacement: [
    nameof<Resources.MatchSearch>(o => o.id),
    nameof.full<Resources.MatchSearch>(o => o.candidate.name),
    nameof.full<Resources.MatchSearch>(o => o.position.name),
    nameof.full<Resources.MatchSearch>(o => o.status.name),
    nameof<Resources.MatchSearch>(o => o.estimatedStartDate),
    nameof<Resources.MatchSearch>(o => o.estimatedEndDate),
    nameof<Resources.MatchSearch>(o => o.actualEndDate),
  ],
};

type ParseTableFilters = (
  filters: Partial<Record<keyof Resources.MatchSearch, string[]>>,
  columns: MatchColumnType[],
) => Resources.MatchSearchFilter;

export const parseTableFilters: ParseTableFilters = (filters, columns) => {
  const filter: Resources.MatchSearchFilter = {};
  Object.keys(filters).forEach(key => {
    const filterColumnKey = key as keyof Resources.MatchSearch;
    const column = columns.find(col => col.key === key);

    if (column && column.filterTransform && column.searchParamsKey) {
      const { filterTransform, searchParamsKey } = column;

      filter[searchParamsKey] = filterTransform(filters[filterColumnKey] || []);
    }
  });
  return filter;
};

export const getDefinedMatchReportColumns = (
  intl: Intl.InjectedIntl,
): Mutations.ColumnDefinition[] => {
  return [
    {
      id: nameof<Resources.MatchSearch>(o => o.status),
      header: intl.formatMessage({ id: messages.status }),
    },
    {
      id: nameof<Resources.MatchSearch>(o => o.estimatedStartDate),
      header: intl.formatMessage({ id: messages.estimatedStartDate }),
    },
    {
      id: nameof<Resources.MatchSearch>(o => o.estimatedEndDate),
      header: intl.formatMessage({ id: messages.estimatedEndDate }),
    },
    {
      id: nameof<Resources.MatchSearch>(o => o.candidate),
      header: intl.formatMessage({ id: messages.candidate }),
    },
    {
      id: nameof<Resources.MatchSearch>(o => o.id),
      header: intl.formatMessage({ id: messages.name }),
    },
    {
      id: nameof<Resources.MatchSearch>(o => o.worksite),
      header: intl.formatMessage({ id: messages.worksite }),
    },
    {
      id: nameof<Resources.MatchSearch>(o => o.facilityDepartments),
      header: intl.formatMessage({ id: messages.facilityDepartments }),
    },
    {
      id: nameof<Resources.MatchSearch>(o => o.position),
      header: intl.formatMessage({ id: messages.position }),
    },
    {
      id: nameof<Resources.MatchSearch>(o => o.company),
      header: intl.formatMessage({ id: messages.company }),
    },
    {
      id: nameof<Resources.MatchSearch>(o => o.createdOn),
      header: intl.formatMessage({ id: messages.createdOn }),
    },
    {
      id: nameof<Resources.MatchSearch>(o => o.contact),
      header: intl.formatMessage({ id: messages.contact }),
    },
    {
      id: nameof<Resources.MatchSearch>(o => o.approvalPath),
      header: intl.formatMessage({ id: messages.approvalPath }),
    },
    {
      id: nameof<Resources.MatchSearch>(o => o.position),
      header: intl.formatMessage({ id: messages.position }),
    },
    {
      id: nameof<Resources.MatchSearch>(o => o.actualEndDate),
      header: intl.formatMessage({ id: messages.actualEndDate }),
    },
    {
      id: nameof<Resources.MatchSearch>(o => o.startConfirmed),
      header: intl.formatMessage({ id: messages.startConfirmed }),
    },
    {
      id: nameof<Resources.MatchSearch>(o => o.vendor),
      header: intl.formatMessage({ id: messages.vendor }),
    },
    {
      id: nameof<Resources.MatchSearch>(o => o.positionType),
      header: intl.formatMessage({ id: messages.positionType }),
    },
    {
      id: nameof<Resources.MatchSearch>(o => o.positionOwner),
      header: intl.formatMessage({ id: messages.positionOwner }),
    },
    {
      id: nameof<Resources.MatchSearch>(o => o.submittedBy),
      header: intl.formatMessage({ id: messages.submittedBy }),
    },
    {
      id: nameof<Resources.MatchSearch>(o => o.submittedOn),
      header: intl.formatMessage({ id: messages.submittedOn }),
    },
  ];
};

type GenerateColumns = (
  filterByDate: OnDateRangeFilter,
  resetDateFilter: ResetDateFilter,
  filterState: Resources.MatchSearchFilter,
  searchQuery: string,
  filterData?: MatchSearchFilterData,
  matchType?: MatchParams,
) => MatchColumnType[];

const MatchStatus = injectIntl(
  ({
    intl,
    activityStatus,
    status,
  }: Resources.MatchSearch & Intl.InjectedIntlProps) => {
    const [color, type] = useMemo<[Status, Icons]>(() => {
      if (activityStatus === Resources.MatchActivityStatus.PendingStart)
        return ['success', 'check'];
      if (status.id === 0) return ['critical', 'close'];
      if (status.id >= 10 && status.id <= 100) return ['pending', 'pending'];
      if (status.id >= 100) return ['success', 'check'];
      return ['standard', 'check'];
    }, [activityStatus, status.id]);

    return (
      <Flex align="center" gutter={2} justify="left">
        <FlexItem flex={0}>
          <Tag circle color={color}>
            <Icon type={type} color="bodyTertiary" size="small" />
          </Tag>
        </FlexItem>
        <FlexItem flex={0}>
          {activityStatus != null
            ? `${status?.name} (${intl.formatMessage({
                id: strToLocalizationKeys(
                  `vms.matchSearch.activityStatus.${Resources.MatchActivityStatus[activityStatus]}`,
                ),
              })})`
            : status?.name}
        </FlexItem>
      </Flex>
    );
  },
);

const filterTransform = (values: string[]) => values.map(Number);

const getStatusColumn = (
  filterState: Resources.MatchSearchFilter,
): MatchColumnType => {
  return {
    title: <FormattedMessage id={messages.status} />,
    key: nameof.full<Resources.MatchSearch>(o => o.status.name),
    sorter: true,
    searchParamsKey: nameof<Resources.MatchSearchFilter>(
      o => o.statusIds,
    ) as keyof SearchParamsKey,
    filteredValue: filterState.statusIds || [],
    filterDropdown: props => (
      <ColumnFilters
        {...props}
        request={Requests.MatchStatusRequest}
        filterData={(statuses: Resources.MatchStatus[]) =>
          statuses.filter(
            status =>
              status.id === REJECTED_STATUS_ID ||
              status.id >= SUBMITTAL_STATUS_ID,
          )
        }
      />
    ),
    filterTransform,
    render: (value, props: Resources.MatchSearch) => <MatchStatus {...props} />,
  };
};

const getEstimatedStartDateColumn = (
  filterState: Resources.MatchSearchFilter,
  resetDateFilter: ResetDateFilter,
  filterByDate: OnDateRangeFilter,
): MatchColumnType => {
  return {
    title: <FormattedMessage id={messages.estimatedStartDate} />,
    dataIndex: nameof<Resources.MatchSearch>(o => o.estimatedStartDate),
    key: nameof<Resources.MatchSearch>(o => o.estimatedStartDate),
    sorter: true,
    filteredValue: filterState.estimatedStartDate
      ? [filterState.estimatedStartDate.from, filterState.estimatedStartDate.to]
      : [],
    render: dateRender,
    filterDropdown: ({ confirm }) => (
      <TableDateRangeFilter
        dateFieldKey={nameof<Resources.MatchSearch>(o => o.estimatedStartDate)}
        range={filterState.estimatedStartDate}
        resetFilter={(dateFieldKey: string) => {
          confirm && confirm();
          resetDateFilter(dateFieldKey);
        }}
        onFilter={(dateFieldKey, dateRange) => {
          confirm && confirm();
          filterByDate(dateFieldKey, dateRange);
        }}
      />
    ),
    permission: AppPermissions.ViewPositionSchedule,
  };
};

const getEstimatedEndDateColumn = (
  filterState: Resources.MatchSearchFilter,
  resetDateFilter: ResetDateFilter,
  filterByDate: OnDateRangeFilter,
): MatchColumnType => {
  return {
    title: <FormattedMessage id={messages.estimatedEndDate} />,
    dataIndex: nameof<Resources.MatchSearch>(o => o.estimatedEndDate),
    key: nameof<Resources.MatchSearch>(o => o.estimatedEndDate),
    sorter: true,
    filteredValue: filterState.estimatedEndDate
      ? [filterState.estimatedEndDate.from, filterState.estimatedEndDate.to]
      : [],
    render: dateRender,
    filterDropdown: ({ confirm }) => (
      <TableDateRangeFilter
        dateFieldKey={nameof<Resources.MatchSearch>(o => o.estimatedEndDate)}
        range={filterState.estimatedEndDate}
        resetFilter={(dateFieldKey: string) => {
          confirm && confirm();
          resetDateFilter(dateFieldKey);
        }}
        onFilter={(dateFieldKey, dateRange) => {
          confirm && confirm();
          filterByDate(dateFieldKey, dateRange);
        }}
      />
    ),
    permission: AppPermissions.ViewPositionSchedule,
  };
};

const getCandidateColumn = (): MatchColumnType => {
  return {
    title: 'Name',
    key: nameof.full<Resources.MatchSearch>(o => o.candidate.name),
    sorter: true,
    render: ({ candidate: { name } }: Resources.MatchSearch) => {
      return <ThemedLink to={''}>{name}</ThemedLink>;
    },
  };
};

export const MatchStatusColumns = {
  getCandidateColumn,
  getStatusColumn,
  getEstimatedStartDateColumn,
  getEstimatedEndDateColumn,
};

export const generateColumns: GenerateColumns = (
  filterByDate,
  resetDateFilter,
  filterState,
  searchQuery,
  filterData,
  matchType,
) => {
  const nameColumn: MatchColumnType = {
    title: <FormattedMessage id={messages.name} />,
    key: nameof<Resources.MatchSearch>(o => o.id),
    sorter: true,
    render: (value: string, { id }: Resources.MatchSearch) => (
      <Authorize permission={AppPermissions.ViewMatch}>
        {(isAuthorized: boolean) =>
          isAuthorized ? (
            <ThemedLink to={`/matches/${id}${searchQuery}`}>{id}</ThemedLink>
          ) : (
            <Text>{id}</Text>
          )
        }
      </Authorize>
    ),
    fixed: 'left',
  };
  const candidateColumn: MatchColumnType = {
    title: <FormattedMessage id={messages.candidate} />,
    key: nameof.full<Resources.MatchSearch>(o => o.candidate.name),
    dataIndex: nameof.full<Resources.MatchSearch>(o => o.candidate.name),
    sorter: true,
    searchParamsKey: nameof<Resources.MatchSearchFilter>(
      o => o.candidateIds,
    ) as keyof SearchParamsKey,
    filteredValue: filterState.candidateIds || [],
    filterDropdown: props => (
      <ColumnFilters {...props} request={Requests.CandidateRequest} />
    ),
    filterTransform,
    render: (name, record: Resources.MatchSearch) => (
      <Flex gutter={2} direction="column">
        <Text>{name}</Text>
        <Text color="bodySecondary">{record.vendor.name}</Text>
      </Flex>
    ),
  };

  const statusColumn: MatchColumnType = getStatusColumn(filterState);

  const facilityDepartmentsColumn: MatchColumnType = {
    title: <FormattedMessage id={messages.facilityDepartments} />,
    key: nameof<Resources.MatchSearch>(o => o.facilityDepartments),
    searchParamsKey: nameof<Resources.MatchSearchFilter>(
      o => o.facilityDepartmentIds,
    ) as keyof SearchParamsKey,
    filteredValue: filterState.facilityDepartmentIds || [],
    filterDropdown: props => (
      <ColumnFilters {...props} request={Requests.FacilityDepartmentRequest} />
    ),
    filterTransform,
    render: ({
      facilityDepartments,
    }: {
      facilityDepartments: Resources.MatchSearch['facilityDepartments'];
    }) => {
      return <Departments departments={facilityDepartments} />;
    },
  };

  const worksiteColumn: MatchColumnType = {
    title: <FormattedMessage id={messages.worksite} />,
    dataIndex: nameof.full<Resources.MatchSearch>(o => o.worksite.name),
    key: nameof.full<Resources.MatchSearch>(o => o.worksite.name),
    sorter: true,
    searchParamsKey: nameof<Resources.MatchSearchFilter>(
      o => o.worksiteIds,
    ) as keyof SearchParamsKey,
    filteredValue: filterState.worksiteIds || [],
    filterDropdown: props => (
      <ColumnFilters {...props} request={Requests.WorksiteRequest} />
    ),
    filterTransform,
    permission: AppPermissions.ViewPositionLocation,
  };

  const companyColumn: MatchColumnType = {
    title: <FormattedMessage id={messages.company} />,
    key: nameof.full<Resources.MatchSearch>(o => o.company.name),
    dataIndex: nameof.full<Resources.MatchSearch>(o => o.company.name),
    sorter: true,
    searchParamsKey: nameof<Resources.MatchSearchFilter>(
      o => o.companyIds,
    ) as keyof SearchParamsKey,
    filteredValue: filterState.companyIds || [],
    filterDropdown: props => (
      <ColumnFilters {...props} request={Requests.CompanyRequest} />
    ),
    filterTransform,
  };

  const contactColumn: MatchColumnType = {
    title: <FormattedMessage id={messages.contact} />,
    dataIndex: nameof.full<Resources.MatchSearch>(o => o.contact.name),
    key: nameof.full<Resources.MatchSearch>(o => o.contact.name),
    sorter: true,
    searchParamsKey: nameof<Resources.MatchSearchFilter>(
      o => o.contactIds,
    ) as keyof SearchParamsKey,
    filteredValue: filterState.contactIds || [],
    filterDropdown: props => (
      <ColumnFilters {...props} request={Requests.ContactRequest} />
    ),
    filterTransform,
  };

  const approvalPathColumn: MatchColumnType = {
    title: <FormattedMessage id={messages.approvalPath} />,
    dataIndex: nameof<Resources.MatchSearch>(o => o.approvalPath),
    key: nameof<Resources.MatchSearch>(o => o.approvalPath),
    filteredValue: filterState.approvalStatuses || [],
    filters: Object.keys(Resources.ApprovalStatus)
      .filter(key => !isNaN(Number(key)))
      .map(key => Number(key))
      .map(approvalStatus => ({
        text: (
          <FormattedMessage
            id={
              ApprovalPathStatuses[approvalStatus as Resources.ApprovalStatus]
            }
          />
        ),
        value: String(approvalStatus),
      })),
    filterTransform: filterTransform,
    searchParamsKey: nameof<Resources.MatchSearchFilter>(
      o => o.approvalStatuses,
    ) as keyof SearchParamsKey,
    render: (value, { approvalPath }: Resources.MatchSearch) =>
      renderApprovalPathColumn(approvalPath),
  };

  const estimatedStartDateColumn: MatchColumnType = getEstimatedStartDateColumn(
    filterState,
    resetDateFilter,
    filterByDate,
  );

  const estimatedEndDateColumn: MatchColumnType = getEstimatedEndDateColumn(
    filterState,
    resetDateFilter,
    filterByDate,
  );

  const currentlyWorkingColumns: MatchColumnType[] = [
    nameColumn,
    candidateColumn,
    {
      title: <FormattedMessage id={messages.position} />,
      key: nameof.full<Resources.MatchSearch>(o => o.position.name),
      sorter: true,
      searchParamsKey: nameof<Resources.MatchSearchFilter>(
        o => o.positionIds,
      ) as keyof SearchParamsKey,
      filteredValue: filterState.positionIds || [],
      filterDropdown: props => (
        <ColumnFilters {...props} request={Requests.PositionRequest} />
      ),
      filterTransform,
      render: (value, { position }: Resources.MatchSearch) => (
        <Authorize
          permission={`${AppPermissions.ViewPositions} & ${AppPermissions.ViewPosition}`}
        >
          {(isAuthorized: boolean) =>
            isAuthorized ? (
              <ThemedLink
                to={`/matches/position/${position?.id}${searchQuery}`}
              >
                {position?.name}
              </ThemedLink>
            ) : (
              <Text>{position?.name}</Text>
            )
          }
        </Authorize>
      ),
    },
    statusColumn,
    contactColumn,
    estimatedStartDateColumn,
    estimatedEndDateColumn,
    {
      title: <FormattedMessage id={messages.actualEndDate} />,
      dataIndex: nameof<Resources.MatchSearch>(o => o.actualEndDate),
      key: nameof<Resources.MatchSearch>(o => o.actualEndDate),
      sorter: true,
      filteredValue: filterState.actualEndDate
        ? [filterState.actualEndDate.from, filterState.actualEndDate.to]
        : [],
      render: dateRender,
      filterDropdown: ({ confirm }) => (
        <TableDateRangeFilter
          dateFieldKey={nameof<Resources.MatchSearch>(o => o.actualEndDate)}
          range={filterState.actualEndDate}
          resetFilter={(dateFieldKey: string) => {
            confirm && confirm();
            resetDateFilter(dateFieldKey);
          }}
          onFilter={(dateFieldKey, dateRange) => {
            confirm && confirm();
            filterByDate(dateFieldKey, dateRange);
          }}
        />
      ),
      permission: AppPermissions.ViewPositionSchedule,
    },
    {
      title: <FormattedMessage id={messages.startConfirmed} />,
      dataIndex: nameof<Resources.MatchSearch>(o => o.startConfirmed),
      key: nameof<Resources.MatchSearch>(o => o.startConfirmed),
      sorter: true,
      render: (prop: boolean) => {
        return <Text>{prop ? 'Yes' : 'No'}</Text>;
      },
    },
    {
      title: <FormattedMessage id={messages.vendor} />,
      dataIndex: nameof.full<Resources.MatchSearch>(o => o.vendor.name),
      key: nameof.full<Resources.MatchSearch>(o => o.vendor.name),
      searchParamsKey: nameof<Resources.MatchSearchFilter>(
        o => o.vendorIds,
      ) as keyof SearchParamsKey,
      filteredValue: filterState.vendorIds,
      filterDropdown: props => {
        return <ColumnFilters {...props} request={Requests.VendorRequest} />;
      },
      filterTransform,
      sorter: true,
    },
    facilityDepartmentsColumn,
    worksiteColumn,
    companyColumn,
    approvalPathColumn,
  ];

  const submittalsColumns: MatchColumnType[] = [
    nameColumn,
    candidateColumn,
    {
      title: <FormattedMessage id={messages.positionCompany} />,
      key: nameof.full<Resources.MatchSearch>(o => o.position.name),
      sorter: true,
      render: (value, { position, company }: Resources.MatchSearch) => (
        <>
          <Authorize
            permission={`${AppPermissions.ViewPositions} & ${AppPermissions.ViewPosition}`}
          >
            {(isAuthorized: boolean) =>
              isAuthorized ? (
                <ThemedLink
                  to={`/matches/position/${position?.id}${searchQuery}`}
                >
                  {position?.name}
                </ThemedLink>
              ) : (
                <Text>{position?.name}</Text>
              )
            }
          </Authorize>
          <Text>{company?.name}</Text>
        </>
      ),
    },
    statusColumn,
    {
      title: <FormattedMessage id={messages.positionType} />,
      key: nameof.full<Resources.MatchSearch>(o => o.positionType.name),
      dataIndex: nameof.full<Resources.MatchSearch>(o => o.positionType.name),
      sorter: true,
    },
    estimatedStartDateColumn,
    facilityDepartmentsColumn,
    {
      title: <FormattedMessage id={messages.positionOwner} />,
      key: nameof.full<Resources.MatchSearch>(o => o.positionOwner.name),
      dataIndex: nameof.full<Resources.MatchSearch>(o => o.positionOwner.name),
      sorter: true,
      searchParamsKey: nameof<Resources.MatchSearchFilter>(
        o => o.positionOwnerIds,
      ) as keyof SearchParamsKey,
      filteredValue: filterState.positionOwnerIds || [],
      filters: filterData?.positionOwners.map(positionOwner => ({
        text: positionOwner.name,
        value: String(positionOwner.id),
      })),
      filterTransform,
    },
    worksiteColumn,
    companyColumn,
    contactColumn,
    {
      title: <FormattedMessage id={messages.submittedBy} />,
      dataIndex: nameof<Resources.MatchSearch>(o => o.submittedBy),
      key: nameof<Resources.MatchSearch>(o => o.submittedBy),
      sorter: true,
      render: (prop: Resources.MatchSearch['submittedBy']) => {
        return <Text>{prop.name}</Text>;
      },
    },
    {
      title: <FormattedMessage id={messages.submittedOn} />,
      dataIndex: nameof<Resources.MatchSearch>(o => o.submittedOn),
      key: nameof<Resources.MatchSearch>(o => o.submittedOn),
      sorter: true,
      render: (value?: string, record?: Resources.MatchSearch) => (
        <Tooltip
          title={
            <>
              <FormattedMessage id={messageIds.vms.matchSearch.submittedBy} />
              {`: ${record ? record.submittedBy.name : '-'}`}
            </>
          }
        >
          <span>{value ? <Intl.FormattedDate value={value} /> : '-'}</span>
        </Tooltip>
      ),
    },
    approvalPathColumn,
  ];

  const columnsForAll: MatchColumnType[] = [
    ...currentlyWorkingColumns,
    {
      title: <FormattedMessage id={messages.createdOn} />,
      dataIndex: nameof<Resources.MatchSearch>(o => o.createdOn),
      key: nameof<Resources.MatchSearch>(o => o.createdOn),
      sorter: true,
      filteredValue: filterState.createdOn
        ? [filterState.createdOn.from, filterState.createdOn.to]
        : [],
      render: dateRender,
      filterDropdown: ({ confirm }) => (
        <TableDateRangeFilter
          dateFieldKey="createdOn"
          range={filterState.createdOn}
          resetFilter={(dateFieldKey: string) => {
            confirm && confirm();
            resetDateFilter(dateFieldKey);
          }}
          onFilter={(dateFieldKey, dateRange) => {
            confirm && confirm();
            filterByDate(dateFieldKey, dateRange);
          }}
        />
      ),
    },
  ];

  switch (matchType) {
    case 'FullPlacement':
      return currentlyWorkingColumns;
    case 'Submittal':
      return submittalsColumns;
    case 'All':
    case 'EndingSoon':
    case 'PendingStart':
    case 'PendingMyApproval':
      return columnsForAll;
    default:
      return [];
  }
};
